import React, { useState, useEffect } from 'react';
import { Player, Position } from '../../types';
import { PositionRankingList } from './PositionRankingList';
import { calculateRatingsForRankedList } from '../../utils/rankingCalculations';
import { Button } from '../common/Button';
import { Card } from '../common/Card';

interface DragRankingInterfaceProps {
  players: Player[];
  onSaveRankings: (playerId: string, position: Position, rating: number | null) => Promise<void>;
}

type PositionKey = 'FWD' | 'MID' | 'DEF' | 'GK';

export function DragRankingInterface({
  players,
  onSaveRankings,
}: DragRankingInterfaceProps) {
  const [activePosition, setActivePosition] = useState<PositionKey>('FWD');
  const [rankedPlayers, setRankedPlayers] = useState<Record<PositionKey, string[]>>({
    FWD: [],
    MID: [],
    DEF: [],
    GK: [],
  });
  const [saving, setSaving] = useState(false);

  // Initialize ranked players based on calculated ratings
  useEffect(() => {
    const initializeRankings = () => {
      const positions: PositionKey[] = ['FWD', 'MID', 'DEF', 'GK'];
      const initialRankings: Record<PositionKey, string[]> = {
        FWD: [],
        MID: [],
        DEF: [],
        GK: [],
      };

      positions.forEach((pos) => {
        // Check if players have manual ratings for this position
        const playersWithManualRatings = players.filter((p) => {
          const manualRating = pos === 'FWD'
            ? p.attributes?.manual_fwd_rating
            : pos === 'MID'
            ? p.attributes?.manual_mid_rating
            : pos === 'DEF'
            ? p.attributes?.manual_def_rating
            : p.attributes?.manual_gk_rating;
          return manualRating !== null && manualRating !== undefined;
        });

        if (playersWithManualRatings.length > 0) {
          // Sort by manual rating (descending)
          const sorted = [...players].sort((a, b) => {
            const aRating = pos === 'FWD'
              ? (a.attributes?.manual_fwd_rating ?? 0)
              : pos === 'MID'
              ? (a.attributes?.manual_mid_rating ?? 0)
              : pos === 'DEF'
              ? (a.attributes?.manual_def_rating ?? 0)
              : (a.attributes?.manual_gk_rating ?? 0);
            const bRating = pos === 'FWD'
              ? (b.attributes?.manual_fwd_rating ?? 0)
              : pos === 'MID'
              ? (b.attributes?.manual_mid_rating ?? 0)
              : pos === 'DEF'
              ? (b.attributes?.manual_def_rating ?? 0)
              : (b.attributes?.manual_gk_rating ?? 0);
            return bRating - aRating;
          });
          initialRankings[pos] = sorted.map((p) => p.id);
        } else {
          // Sort by calculated rating (descending)
          const sorted = [...players].sort((a, b) => {
            const aRating = pos === 'FWD'
              ? (a.calculated?.fwd_rating ?? 0)
              : pos === 'MID'
              ? (a.calculated?.mid_rating ?? 0)
              : pos === 'DEF'
              ? (a.calculated?.def_rating ?? 0)
              : (a.calculated?.gk_rating ?? 0);
            const bRating = pos === 'FWD'
              ? (b.calculated?.fwd_rating ?? 0)
              : pos === 'MID'
              ? (b.calculated?.mid_rating ?? 0)
              : pos === 'DEF'
              ? (b.calculated?.def_rating ?? 0)
              : (b.calculated?.gk_rating ?? 0);
            return bRating - aRating;
          });
          initialRankings[pos] = sorted.map((p) => p.id);
        }
      });

      setRankedPlayers(initialRankings);
    };

    if (players.length > 0) {
      initializeRankings();
    }
  }, [players]);

  const handleRankingChange = (newRankedPlayerIds: string[]) => {
    setRankedPlayers((prev) => ({
      ...prev,
      [activePosition]: newRankedPlayerIds,
    }));

    // Auto-save on drop
    saveRankingsForPosition(activePosition, newRankedPlayerIds);
  };

  const saveRankingsForPosition = async (position: PositionKey, rankedPlayerIds: string[]) => {
    setSaving(true);
    try {
      const ratings = calculateRatingsForRankedList(rankedPlayerIds);

      // Save each player's rating
      await Promise.all(
        rankedPlayerIds.map((playerId) =>
          onSaveRankings(playerId, position, ratings[playerId])
        )
      );
    } catch (error) {
      console.error('Failed to save rankings:', error);
      alert('Failed to save rankings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPosition = async () => {
    if (!confirm(`Reset all ${activePosition} rankings? This will clear manual ratings for this position.`)) {
      return;
    }

    setSaving(true);
    try {
      // Clear manual ratings for this position
      await Promise.all(
        players.map((player) => onSaveRankings(player.id, activePosition, null))
      );

      // Re-sort by calculated rating
      const sorted = [...players].sort((a, b) => {
        const aRating = activePosition === 'FWD'
          ? (a.calculated?.fwd_rating ?? 0)
          : activePosition === 'MID'
          ? (a.calculated?.mid_rating ?? 0)
          : activePosition === 'DEF'
          ? (a.calculated?.def_rating ?? 0)
          : (a.calculated?.gk_rating ?? 0);
        const bRating = activePosition === 'FWD'
          ? (b.calculated?.fwd_rating ?? 0)
          : activePosition === 'MID'
          ? (b.calculated?.mid_rating ?? 0)
          : activePosition === 'DEF'
          ? (b.calculated?.def_rating ?? 0)
          : (b.calculated?.gk_rating ?? 0);
        return bRating - aRating;
      });

      setRankedPlayers((prev) => ({
        ...prev,
        [activePosition]: sorted.map((p) => p.id),
      }));
    } catch (error) {
      console.error('Failed to reset rankings:', error);
      alert('Failed to reset rankings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyToAllPositions = async () => {
    if (!confirm(`Apply ${activePosition} ranking order to all other positions? This will overwrite existing rankings.`)) {
      return;
    }

    setSaving(true);
    try {
      const currentRanking = rankedPlayers[activePosition];
      const positions: PositionKey[] = ['FWD', 'MID', 'DEF', 'GK'];

      // Calculate ratings for current ranking
      const ratings = calculateRatingsForRankedList(currentRanking);

      // Apply to all positions
      await Promise.all(
        positions.flatMap((pos) =>
          currentRanking.map((playerId) =>
            onSaveRankings(playerId, pos, ratings[playerId])
          )
        )
      );

      // Update state
      setRankedPlayers({
        FWD: currentRanking,
        MID: currentRanking,
        DEF: currentRanking,
        GK: currentRanking,
      });
    } catch (error) {
      console.error('Failed to apply rankings:', error);
      alert('Failed to apply rankings to all positions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const positions: { key: PositionKey; label: string }[] = [
    { key: 'FWD', label: 'Forward' },
    { key: 'MID', label: 'Midfielder' },
    { key: 'DEF', label: 'Defender' },
    { key: 'GK', label: 'Goalkeeper' },
  ];

  return (
    <Card>
      <div className="space-y-6">
        {/* Position Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {positions.map((pos) => (
              <button
                key={pos.key}
                onClick={() => setActivePosition(pos.key)}
                className={`${
                  activePosition === pos.key
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                {pos.key}
              </button>
            ))}
          </nav>
        </div>

        {/* Ranking List */}
        {players.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No players yet. Add players to start ranking!
          </div>
        ) : (
          <>
            <PositionRankingList
              players={players}
              position={activePosition}
              rankedPlayerIds={rankedPlayers[activePosition]}
              onRankingChange={handleRankingChange}
            />

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="ghost"
                onClick={handleResetPosition}
                disabled={saving}
              >
                Reset {activePosition} Rankings
              </Button>
              <Button
                variant="secondary"
                onClick={handleApplyToAllPositions}
                disabled={saving}
              >
                Apply to All Positions
              </Button>
            </div>

            {saving && (
              <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                Saving rankings...
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
